import { CSSProperties, MutableRefObject, ReactNode, FocusEventHandler, MouseEventHandler } from 'react';
import {
	CARD_TYPE,
	COMPONENT_STATES,
	COMPONENT_TYPE,
	DEFAULT_MEDIA_QUERY_PLATFORMS,
	ELEMENT_EDIT_MODE,
	LAYER_TYPE,
	PIN,
	PIN_TYPE,
	UNIFORM_PROPS,
	CONNECTION_TYPES,
	STORY_TYPE,
	CONTENT_ALIGN_Y,
	CARD_TINT,
} from 'common/constants';
import { IOrganizationFonts, IOrganizationIntegrations } from 'types/organization';
import { FieldBBErrorType, FieldBBType } from 'client/components/common/BuildingBlocks/Fields/constants';
import { TransitionHandlerContextType } from 'client/components/common/CardTransition/TransitionHandler';
import { ITraverseTreeOptionsListeners } from 'client/components/common/BuildingBlocks/utils/traverse-tree-types';
import { SHARE_TYPE } from 'client/components/common/BuildingBlocks/Share/constants';
import {
	ANIMATE_DIRECTION,
	SHAPE_LINE_DIRECTION,
	SHAPE_TYPE,
} from 'client/components/common/BuildingBlocks/Shape/Shape';
import { FormCardContextType } from 'client/components/common/StoryCard/Form/types';
import { AnimationData } from 'client/components/common/BuildingBlocks/animation';
import { TIMER_TYPE } from 'client/components/common/BuildingBlocks/Timer/Timer';
import { BOX_TYPE } from 'client/components/common/BuildingBlocks/Box';
import { SELECTION_STAGE } from 'client/components/common/SelectionHint/utils';
import { IProfilePictureFile } from 'src/types/user';
import { BUTTON_SHAPE_TYPES } from 'client/components/common/BuildingBlocks/Button';
import { CountType } from 'utils/facades/navigation-card-facade';

export enum EditorMode {
	DESIGN = 'DESIGN',
	CONTENT = 'CONTENT',
}

export type ComponentTypes = typeof COMPONENT_TYPE[keyof typeof COMPONENT_TYPE];

export type BBStates = typeof COMPONENT_STATES[keyof typeof COMPONENT_STATES];

export type StoryMediaPlatform = typeof DEFAULT_MEDIA_QUERY_PLATFORMS[keyof typeof DEFAULT_MEDIA_QUERY_PLATFORMS];

export type LayerType = typeof LAYER_TYPE[keyof typeof LAYER_TYPE];

export type WithStateAndPlatform<T, State extends BBStates = BBStates> = Record<State, Record<StoryMediaPlatform, T>>;

export type BBStylesProp = CSSProperties & {
	borderStyle?: 'none' | 'solid' | 'none none solid none';
	backgroundSize?: string;
	backgroundPosition?: string;
};

export type BBOtherProp = {
	boxType?: ValuesType<typeof BOX_TYPE>;
	shapeType?: ValuesType<typeof SHAPE_TYPE>;
	lineDir?: ValuesType<typeof SHAPE_LINE_DIRECTION>;
	lineSize?: string;
	animateDir?: ValuesType<typeof ANIMATE_DIRECTION>;
	backgroundVideo?: string;
	backgroundPoster?: string;
	videoSrc?: string;
	videoPoster?: string;
	muted?: boolean;
	loop?: boolean;
	controls?: boolean;
	autoplay?: boolean;
	[PIN_TYPE]?: ValuesType<typeof PIN>;
	[CARD_TINT]?: string;
	embedCode?: string;
	contentAlignY?: ValuesType<typeof CONTENT_ALIGN_Y>;
	contentHeight?: string;
	contentMarginTop?: string;
	contentMarginBottom?: string;
	contentMarginLeft?: string;
	contentMarginRight?: string;
	buttonShapeType?: ValuesType<typeof BUTTON_SHAPE_TYPES>;
};

export type BBAnimationProp = WithStateAndPlatform<Record<string, AnimationData>, 'defaultState'>;

/**
 * @info: client building block property
 */
export type BBUiConfig = {
	layer?: LayerType;
	editorProps: {
		name: string;
		selectable: boolean;
		draggable: boolean;
		expandable: boolean;
		expanded?: boolean;
		[UNIFORM_PROPS.fieldErrorShow]?: string;
	};
	componentProps: {
		// TODO: styles and other are optional for symbol instances!
		// TODO: styles & other change to WithStateAndPlatform<DataType>
		styles: BBStylesProp;
		other: BBOtherProp;
		animation?: BBAnimationProp;

		// custom props
		btnLink?: string;
		btnNavLink?: string;
		btnTarget?: boolean;
		shareType?: typeof SHARE_TYPE[keyof typeof SHARE_TYPE];
		fieldType?: FieldBBType; // UNIFORM_PROPS.fieldType
		fieldError?: Record<FieldBBErrorType, string>; // UNIFORM_PROPS.fieldError
		[UNIFORM_PROPS.fieldRequired]?: boolean;
		[UNIFORM_PROPS.fieldFullName]?: boolean;
		[UNIFORM_PROPS.title]?: string;
		[UNIFORM_PROPS.shareCard]?: boolean;
		// value in seconds
		[UNIFORM_PROPS.answerTimeout]?: number | string;

		// Bind any of swipe actions to some component by id, to be able to call his handler on demand
		[UNIFORM_PROPS.onSwipe]?: {
			// Component id.
			left?: string;
			// Component id
			right?: string;
		};

		// Timer BB
		[UNIFORM_PROPS.timerTime]?: number;
		[UNIFORM_PROPS.timerType]?: typeof TIMER_TYPE[keyof typeof TIMER_TYPE];
		[UNIFORM_PROPS.tag]?: string;
	};
	nodeProps: {
		id: string;
		className?: string;
		style?: CSSProperties;
		// provided to rendered blocks at client side
		'data-bb'?: ComponentTypes;
	};
};

type BBComponentProps = BBUiConfig['componentProps'];

export type BBComponentPropsInheritance = Partial<
	Record<keyof Omit<BBComponentProps, 'styles' | 'other'>, BBInheritanceValue[] | undefined>
> &
	Record<
		keyof Pick<BBComponentProps, 'styles'>,
		Partial<Record<keyof BBComponentProps['styles'], BBInheritanceValue[] | undefined>>
	> &
	Record<
		keyof Pick<BBComponentProps, 'other'>,
		Partial<Record<keyof BBComponentProps['other'], BBInheritanceValue[] | undefined>>
	>;

export type BBInheritanceValue = {
	secondValue?: any;
	value?: any;
	state?: BBStates;
	mq?: StoryMediaPlatform;
};

export type BBInheritanceParent = {
	_id: string;
	path: string;
	type: ComponentTypes;
	name: string;
	inheritance: {
		componentProps?: BBComponentPropsInheritance;
	};
};

/**
 * @info: props passed by traverseTree to client components (building blocks), in case target is editor
 */
export type BBEditableModeProps = {
	nodeProps?: {
		'data-path': string;
		'data-pin': string;
		'data-lock': boolean;
		className?: string;
		style?: CSSProperties;
	};
	eventListeners?: Partial<ITraverseTreeOptionsListeners>;
	inheritance?: {
		componentProps: BBComponentPropsInheritance;
		parent: BBInheritanceParent[];
	};
};

export type BBSymbolLink = { symbol: BBSymbolInfo };

export type BBModelTextChild = WithStateAndPlatform<string, 'defaultState'>;

export type BBModel = {
	_id: string;
	type: ComponentTypes;
	uiConfig: BBUiConfig;
	children: BBModel[] | BBModelTextChild;
} & Partial<BBSymbolLink>;

export type BBSymbolInstance = BBModel & BBSymbolLink;

export type CardFlowEventType = typeof CONNECTION_TYPES[keyof typeof CONNECTION_TYPES];

/**
 * @info: src/routes/admin/components/pages/Story/Flow/Connector/ConnectorSvg/utils.js -> CardEvent
 */
export type CardFlowEvent = {
	_id: string;
	name: string;
	score?: number[];
	componentId?: string;
	connection?: {
		type: CardFlowEventType;
		pointId?: string;
	};
};

export type CardData = {
	_id: string;
	type: ValuesType<typeof CARD_TYPE>;
	name: string;
	settings: {};
	elements: [
		BBModel & { type: typeof COMPONENT_TYPE.FLOAT_ABOVE; children: BBModel[] },
		BBModel & { type: typeof COMPONENT_TYPE.CONTENT; children: BBModel[] },
		BBModel & { type: typeof COMPONENT_TYPE.FLOAT_BELOW; children: BBModel[] },
		BBModel & { type: typeof COMPONENT_TYPE.CARD; children: BBModel[] }
	];
	events: CardFlowEvent[];
};

export type CardDataWithStepInfo = CardData & { step: { _id: StoryStep['_id'] } };

export type BBSymbolInfo = {
	masterId: string; // "masterId" links to the "_id" of symbol MASTER
	instanceId: string; // "instanceId" links to the "_id" of symbol INSTANCE
	childId?: string; // "childId" links to the "_id" of related child in symbol MASTER
};

export type SelectedBBSingle = {
	_id: string;
	type: ComponentTypes;
	path: string;
	uiConfig: BBUiConfig;
	selectionStage: typeof SELECTION_STAGE[keyof typeof SELECTION_STAGE];
	inheritance: {
		componentProps: BBComponentPropsInheritance;
		parent: BBInheritanceParent[];
	};
} & Partial<BBSymbolLink>;

export type SelectedBB = Record<string, SelectedBBSingle>;

export type EditableStateInfo = {
	state: BBStates;
	source: {
		id: string;
		path: string;
		type: ComponentTypes | '';
		layer: LayerType;
		states?: BBStates[];
	};
};

export type StorySymbol = {
	master: BBModel;
	instance: Record<string, BBSymbolInstance>;
};

export type StorySymbols = Record<string, StorySymbol>;

type BBStatesProp = Record<Exclude<BBStates, typeof COMPONENT_STATES.DEFAULT>, boolean>;

export type BBCommonProps = {
	_id: string;
	uiConfig: BBUiConfig;
	editableModeProps?: BBEditableModeProps;
	children: ReactNode;
	currentMediaQuery: StoryMediaPlatform;
	mediaQuery: StoryMediaQuery;
	states: BBStatesProp; // building block current states (hover, correct, etc.)
	// parent states currently provided only by Answer to his children and by Swipe
	parentStates?: BBStatesProp; // parent building block current states (hover, correct, etc.)
	setStates: (state: Partial<Record<BBStates, boolean>>) => void; // callback to set props.states
	stateAttrs: { [key: string]: string }; // props.states converted as data attributes to assign to DOM node
	eventListeners?:
		| {
				// editor mode
				onDoubleClickCapture: MouseEventHandler;
				onFocus: FocusEventHandler;
				onMouseDown: MouseEventHandler;
				onMouseDownCapture: MouseEventHandler;
				onMouseEnter: MouseEventHandler;
				onMouseLeave: MouseEventHandler;
				onClick?: MouseEventHandler;
		  }
		| {
				// non-editor mode
				onMouseEnter: MouseEventHandler;
				onMouseLeave: MouseEventHandler;
				onClick?: MouseEventHandler;
		  };
	isEditableMode?: boolean;
	contentAlignY?: typeof CONTENT_ALIGN_Y[keyof typeof CONTENT_ALIGN_Y];
	onEditableMouseEnter?: MouseEventHandler;
	onEditableMouseLeave?: MouseEventHandler;
	onEditableClick?: MouseEventHandler;
	onEditableFocus?: FocusEventHandler;
	containerRef?: MutableRefObject<HTMLElement>;
	formContext?: FormCardContextType;
	type: ComponentTypes;
	isCardTree: boolean;
	editorMode: EditorMode;
	isEmbed: boolean;
	answerVote?: {
		isShow: boolean;
		value: number;
		// value in milliseconds
		duration: number;
	};
	getComputedComponentProps: (state: BBStates) => ReturnType<ComputedComponentPropsFn>;
	/**
	 * @desc used to pass props into some BB for reuse it in other building block (e.g. Img in Share)
	 */
	custom?: {
		style: CSSProperties;
	};
} & Partial<BBSymbolLink> &
	TransitionHandlerContextType;

export type BBEditMode = typeof ELEMENT_EDIT_MODE[keyof typeof ELEMENT_EDIT_MODE];

export type CardEditorSymbolInfo = {
	editMode: BBEditMode;
	masterId?: string;
	instanceId?: string;
};

export type StorySingleMediaQueryConfig = {
	maxWidth: number | null;
	minWidth: number | null;
	uiName: string;
};

export type StoryMediaQuery = {
	defaultPlatform: StoryMediaPlatform;
	config: Record<StoryMediaPlatform, StorySingleMediaQueryConfig>;
};

export type StoryStep = {
	_id: string;
	cards: CardData[];
};

type Screenshot = Record<StoryMediaPlatform, { filepath: string; hosting: string }>;

export type StorySettingsType = {
	cardOrder?: string[]; // published version only
	cards?: Record<
		string,
		{
			answers?: {
				[answerId: string]:
					| { name: string; isCorrect?: boolean } // Trivia, TrueOrFalse
					| { name: string; score?: string } // PersonalityTest with "countType: score"
					// PersonalityTest with "countType: character"
					| { name: string; characterPoints?: { [characterId: string]: string } };
			};
			input?: Record<string, { name?: string }>;
			score?: string;
			showCorrect?: boolean; // whether Answer has correct/incorrect states
			showResults?: boolean; // whether to render Answer with a special blocks for live results
			countType?: CountType; // way the Navigation card should count user result
			name?: string;
			seo?: { title?: string; description?: string; keywords?: string };
			share?: { title?: string; description?: string; msg?: string; image?: string };
			// note: not implemented yet
			answersMax?: number;
		}
	>;
	characters?: {
		[characterId: string /* characterId is "event._id" of Navigation card with "countType: character" */]: {
			name: string;
			cardId: string;
		};
	};
	seo?: { title?: string; description?: string; keywords?: string; favicon?: string; webclip?: string };
	share?: { title?: string; description?: string; image?: string };
	fonts?: IOrganizationFonts;
	integrations?: IOrganizationIntegrations;
	restrictions?: { oncePerUser: boolean };
	screenshot?: Record<string, Screenshot>;
};

export type StoryVersionType = {
	storyId: string;
	version: string;
	settings: StorySettingsType;
	createdAt: string;
	updatedAt: string;
	changelog: null;
	url?: string; // exists only on published stories
	data: {
		mediaQuery: StoryMediaQuery;
		elements: BBModel[];
		steps: StoryStep[];
		symbols?: StorySymbols;
		appVersion: string;
	};
};

export type StoryVersions = {
	[key: string]: StoryVersionType;
};

export type StoryModel = {
	id: string;
	clientStoryId: string;
	domainId: null | number;
	teamId: null | string;
	status: 'draft' | 'published';
	createdBy: string;
	organizationId: string;
	member: {
		id: string;
		name: string;
		profilePictureFile: IProfilePictureFile;
		// get-organization-stories response only
		email?: string;
	};
	name: string;
	type: typeof STORY_TYPE[keyof typeof STORY_TYPE] | null; // only old stories have null type
	language: string;
	tags: string[];
	createdAt: string; // ISO
	updatedAt: string; // ISO
	storyVersions: StoryVersions;
	team: {
		id: string;
		name: string;
	};
};

export type CardEditorType = {
	data: CardData | null;
	symbols: StorySymbols;
	currentMediaQuery: StoryVersionType['data']['mediaQuery']['defaultPlatform'];
	cardPath: string;
	state: EditableStateInfo;
	storySettings: StorySettingsType;
	storyElements: BBModel[];
	// synchronized by state-sync-middleware.ts at(ISO time)
	stateSyncAt: null | string;
};

export type DictionaryType = { [key: string]: string };

export type BBOptions = { dictionary: DictionaryType };

export type CardTemplate = {
	organizationId: null | string;
	name: string;
	subtitle: string;
	description: string;
	coverImage?: number | null;
	coverImageFile?: IAssetResource | null;
	hoverImage?: number | null;
	hoverImageFile?: IAssetResource | null;
	editor: {
		card: {
			_id: CardData['_id'];
			type?: CardData['type'];
			name?: CardData['name'];
			elements: CardData['elements'];
			settings: CardData['settings'];
			events: CardData['events'];
		};
		symbols?: StoryVersionType['data']['symbols'];
		storyElements?: StoryVersionType['data']['elements'];
	};
	metadata: {
		storySettings: StoryVersionType['settings'];
		storyId: string; // original story id
		cardId: string; // original card id
		version: string; // app version when template was created
		defaultPlatform: StoryMediaQuery['defaultPlatform']; // mediaQuery.defaultPlatform of the original story
		clientStoryId: StoryModel['clientStoryId'];
		screenshot?: Screenshot; // screenshot path for a new templates
	};
	type: 'card';
	categories: string[];
	language: string;
	id: string;
	isDefault?: boolean;
	createdAt?: string;
	updatedAt?: string;
	isPublic?: boolean;
	memberId?: string | null;
	uiTarget?: typeof STORY_TYPE[keyof typeof STORY_TYPE][] | null;
};

export type ComputedComponentPropsFn = (props: {
	element: BBModel; // note: componentProps with state & platforms (not computed one)
	mediaKeysInOrderOfInheritance: StoryMediaPlatform[];
	state: BBStates;
}) => {
	value: BBModel['uiConfig']['componentProps'];
	inheritance: ReturnType<ComputedValueFn>['inheritance'];
};

export type ComputedValueFn = (props: {
	source?: string | number | boolean | object;
	sourceKey?: string;
	mediaKeysInOrderOfInheritance: StoryMediaPlatform[];
	currState: BBStates;
}) => {
	value: string | boolean | object;
	inheritance: BBComponentPropsInheritance;
};
